import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { CLARITY_ONE_UNAUTHED_ERR_MSG } from "@shared/clarityOneConst";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, loggerLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { cacheServerBuildId } from "./components/ErrorBoundary";
import { initChunkLoadRecoveryForBuild } from "./lib/chunkLoadRecovery";
import { registerChunkLoadRecovery } from "./lib/registerChunkLoadRecovery";
import { getLoginUrl } from "./const";
import { isClarityOneMode, getClarityOneLoginUrl } from "./lib/clarityOneMode";
import { installOperationsControlCapture } from "./lib/cleoOperationsControlContext";
import {
  installToastErrorReporter,
  rememberToastTrpcError,
  trpcOperationFromCacheKey,
} from "./lib/toastErrorReport";
import "./styles/sales-portal-ambience.css";
import "./index.css";

registerChunkLoadRecovery();
initChunkLoadRecoveryForBuild();

// Cache server build commit for ErrorBoundary deploy-mismatch detection.
void (async () => {
  try {
    const input = encodeURIComponent(JSON.stringify({ "0": { json: { timestamp: Date.now() } } }));
    const res = await fetch(`/api/trpc/system.health?batch=1&input=${input}`, {
      headers: { accept: "application/json" },
      credentials: "include",
    });
    if (!res.ok) return;
    const json = await res.json();
    const buildCommit = json?.[0]?.result?.data?.json?.buildCommit;
    if (typeof buildCommit === "string") cacheServerBuildId(buildCommit);
  } catch {
    /* ignore */
  }
})();

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isStaffUnauthed = error.message === UNAUTHED_ERR_MSG;
  const isClarityOneUnauthed = error.message === CLARITY_ONE_UNAUTHED_ERR_MSG;

  if (isClarityOneUnauthed && isClarityOneMode()) {
    window.location.href = getClarityOneLoginUrl();
    return;
  }

  if (isStaffUnauthed && !isClarityOneMode()) {
    window.location.href = getLoginUrl();
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    const operation = trpcOperationFromCacheKey(event.query.queryKey, "unknown.query");
    rememberToastTrpcError({ attemptedAction: `Load ${operation}`, operation, error });
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    const operation = trpcOperationFromCacheKey(event.mutation.options.mutationKey, "unknown.mutation");
    rememberToastTrpcError({ attemptedAction: `Run ${operation}`, operation, error });
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    loggerLink({
      enabled: ({ direction, result }) => direction === "down" && result instanceof Error,
      logger: ({ direction, path, result }) => {
        if (direction !== "down" || !(result instanceof Error)) return;
        rememberToastTrpcError({ attemptedAction: `Run ${path}`, operation: path, error: result });
      },
    }),
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

installOperationsControlCapture();
installToastErrorReporter({
  submit: (input) => trpcClient.cleoOperationsDesk.reportToastError.mutate(input),
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
